export const DENTALLY_MAX_PAGES = 100;
export const DENTALLY_MAX_ITEMS = 10000;

/**
 * Why there are two modes.
 *
 * `DENTALLY_MAX_ITEMS` and the total-pages half of `DENTALLY_MAX_PAGES` are
 * WALK limits. They exist so a page-by-page walk that accumulates every row in
 * memory (fetchDentallyPractitioners, fetchDentallyCalendarDiaries) refuses a
 * result set it would take thousands of round trips to drain, and refuses it up
 * front rather than after a hundred of them.
 *
 * A server-paginated grid walks nothing: it asks for one page and renders it.
 * Applying the walk limits there rejected perfectly good single-page responses
 * purely because the FILTER matched a lot of rows — 157,837 completed
 * appointments on staging practice 7 is 6,314 pages, so four of the seven
 * appointment status filters and the practitioner detail modal failed outright
 * on a HTTP 200.
 *
 * Both modes still refuse a page number beyond `DENTALLY_MAX_PAGES`: the
 * provider will not serve one, and neither will our own query serializer, whose
 * `page` field is `max_value=100`.
 */
export const DENTALLY_PAGE_MODE = Object.freeze({
  WALK: 'walk',
  SINGLE_PAGE: 'single-page',
});

export class DentallyPaginationError extends Error {
  constructor(code, message = 'Dentally returned invalid pagination metadata.') {
    super(message);
    this.name = 'DentallyPaginationError';
    this.code = code;
  }
}

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const fail = (code) => {
  throw new DentallyPaginationError(code);
};

const readInteger = (value, { minimum = 0 } = {}) => {
  if (!Number.isSafeInteger(value) || value < minimum) fail('invalid_pagination');
  return value;
};

const readProviderPage = (meta) => {
  const hasCurrentPage = hasOwn(meta, 'current_page');
  const hasPage = hasOwn(meta, 'page');
  if (!hasCurrentPage && !hasPage) fail('missing_pagination');

  const currentPage = hasCurrentPage
    ? readInteger(meta.current_page, { minimum: 1 })
    : readInteger(meta.page, { minimum: 1 });

  if (hasCurrentPage && hasPage) {
    const page = readInteger(meta.page, { minimum: 1 });
    if (page !== currentPage) fail('page_mismatch');
  }

  return currentPage;
};

const assertCaps = ({ currentPage, total, totalPages, perPage, singlePage }) => {
  // One page is never allowed to be unbounded, in either mode.
  if (perPage > DENTALLY_MAX_ITEMS) fail('item_limit_exceeded');
  // No page beyond the cap can be served, in either mode.
  if (currentPage > DENTALLY_MAX_PAGES) fail('pagination_limit_exceeded');
  if (singlePage) return;
  // Walk mode only: the caller will hold the whole result set.
  if (total !== null && total > DENTALLY_MAX_ITEMS) fail('item_limit_exceeded');
  if (totalPages > DENTALLY_MAX_PAGES) fail('pagination_limit_exceeded');
};

const paginationResult = ({ currentPage, perPage, total, totalPages, nextPage }) => ({
  currentPage,
  perPage,
  total,
  totalPages,
  nextPage,
  hasMore: nextPage !== null,
  complete: nextPage === null,
});

export function parseDentallyPageMeta(meta, {
  requestedPage,
  requestedPerPage,
  rowCount,
  mode = DENTALLY_PAGE_MODE.WALK,
} = {}) {
  const singlePage = mode === DENTALLY_PAGE_MODE.SINGLE_PAGE;
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) fail('missing_pagination');

  const requested = readInteger(requestedPage, { minimum: 1 });
  const rows = readInteger(rowCount);
  const currentPage = readProviderPage(meta);
  const perPage = hasOwn(meta, 'per_page')
    ? readInteger(meta.per_page, { minimum: 1 })
    : readInteger(requestedPerPage, { minimum: 1 });
  const hasTotal = hasOwn(meta, 'total');
  const hasTotalEntries = hasOwn(meta, 'total_entries');
  const hasTotalPages = hasOwn(meta, 'total_pages');
  if (!hasTotal && !hasTotalEntries && !hasTotalPages) fail('missing_pagination');

  const declaredTotal = hasTotal ? readInteger(meta.total) : null;
  const aliasedTotal = hasTotalEntries ? readInteger(meta.total_entries) : null;
  if (hasTotal && hasTotalEntries && declaredTotal !== aliasedTotal) {
    fail('pagination_mismatch');
  }
  const total = hasTotal ? declaredTotal : aliasedTotal;
  const declaredTotalPages = hasTotalPages ? readInteger(meta.total_pages) : null;
  let derivedTotalPages = null;
  if (total !== null) {
    derivedTotalPages = total === 0 ? 0 : Math.ceil(total / perPage);
  }
  const usesBackendEmptyPageConvention = total === 0 && declaredTotalPages === 1;
  if (declaredTotalPages !== null
      && derivedTotalPages !== null
      && declaredTotalPages !== derivedTotalPages
      && !usesBackendEmptyPageConvention) {
    fail('pagination_mismatch');
  }
  const totalPages = declaredTotalPages ?? derivedTotalPages;

  assertCaps({ currentPage, total, totalPages, perPage, singlePage });
  if (requested > DENTALLY_MAX_PAGES) fail('pagination_limit_exceeded');
  if (currentPage !== requested) fail('page_mismatch');

  if ((totalPages === 0 && currentPage !== 1)
      || (totalPages > 0 && currentPage > totalPages)) {
    fail('pagination_mismatch');
  }
  if (rows > perPage) fail('row_count_exceeds_page');

  const pageStart = (currentPage - 1) * perPage;
  if (total !== null && pageStart + rows > total) fail('row_count_exceeds_total');
  if (rows === 0 && currentPage < totalPages) fail('empty_page_before_end');

  let nextPage = currentPage < totalPages ? currentPage + 1 : null;
  if (hasOwn(meta, 'next_page')) {
    if (meta.next_page === null) {
      if (nextPage !== null) fail('next_page_mismatch');
    } else {
      const declaredNextPage = readInteger(meta.next_page, { minimum: 1 });
      if (!singlePage && declaredNextPage > DENTALLY_MAX_PAGES) fail('pagination_limit_exceeded');
      if (declaredNextPage !== nextPage) fail('next_page_mismatch');
      nextPage = declaredNextPage;
    }
  }
  // A result set deeper than the cap is real, and `total` still reports it
  // honestly; there is simply no next page the provider will serve.
  if (singlePage && nextPage !== null && nextPage > DENTALLY_MAX_PAGES) nextPage = null;

  return paginationResult({ currentPage, perPage, total, totalPages, nextPage });
}

const readNextUrlInteger = (url, key, mismatchCode) => {
  const values = url.searchParams.getAll(key);
  if (values.length !== 1 || !/^(0|[1-9]\d*)$/.test(values[0])) fail(mismatchCode);
  return readInteger(Number(values[0]));
};

export function parseDentallyOffsetPage(payload, {
  limit,
  offset,
  mode = DENTALLY_PAGE_MODE.WALK,
} = {}) {
  const singlePage = mode === DENTALLY_PAGE_MODE.SINGLE_PAGE;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    fail('missing_pagination');
  }
  ['count', 'next', 'results'].forEach((key) => {
    if (!hasOwn(payload, key)) fail('missing_pagination');
  });

  const perPage = readInteger(limit, { minimum: 1 });
  const currentOffset = readInteger(offset);
  const total = readInteger(payload.count);
  if (!Array.isArray(payload.results)) fail('invalid_pagination');
  if (currentOffset % perPage !== 0) fail('invalid_pagination');

  const totalPages = total === 0 ? 0 : Math.ceil(total / perPage);
  const currentPage = currentOffset / perPage + 1;
  assertCaps({ currentPage, total, totalPages, perPage, singlePage });

  if ((totalPages === 0 && currentOffset !== 0)
      || (totalPages > 0 && currentPage > totalPages)) {
    fail('pagination_mismatch');
  }
  if (payload.results.length > perPage) fail('row_count_exceeds_page');
  if (currentOffset + payload.results.length > total) fail('row_count_exceeds_total');
  const expectedRowCount = Math.min(perPage, total - currentOffset);
  if (payload.results.length !== expectedRowCount) fail('pagination_mismatch');

  const shouldHaveNext = currentPage < totalPages;
  if (payload.results.length === 0 && shouldHaveNext) fail('empty_page_before_end');
  if (shouldHaveNext && (typeof payload.next !== 'string' || payload.next.length === 0)) {
    fail('next_page_mismatch');
  }
  if (!shouldHaveNext && payload.next !== null) fail('next_page_mismatch');

  let nextPage = null;
  if (shouldHaveNext) {
    let nextUrl;
    try {
      nextUrl = new URL(payload.next, 'https://crm.invalid');
    } catch {
      fail('invalid_pagination');
    }

    const nextOffset = readNextUrlInteger(nextUrl, 'offset', 'offset_mismatch');
    if (nextOffset !== currentOffset + perPage) fail('offset_mismatch');
    const nextLimit = readNextUrlInteger(nextUrl, 'limit', 'pagination_mismatch');
    if (nextLimit !== perPage) fail('pagination_mismatch');
    nextPage = currentPage + 1;
    if (singlePage && nextPage > DENTALLY_MAX_PAGES) nextPage = null;
  }

  return paginationResult({ currentPage, perPage, total, totalPages, nextPage });
}
